import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/App';
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Cart = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem, getTotal, clearCart } = useCart();

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex flex-col">
        <header className="sticky top-0 z-50 bg-[#0a0a0f] border-b border-white/5">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => navigate(`/${slug}`)}
                className="text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="font-bold text-white">Carrinho</h1>
            </div>
          </div>
        </header>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <ShoppingBag className="w-16 h-16 text-white/20 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Carrinho vazio</h2>
          <p className="text-white/60 mb-6">Adicione produtos para continuar</p>
          <Button className="btn-primary" onClick={() => navigate(`/${slug}`)}>
            Ver Cardápio
          </Button>
        </div>
      </div>
    );
  }

  const getItemTotal = (item) => {
    const addonsTotal = (item.addons || []).reduce((sum, addon) => sum + addon.price * addon.quantity, 0);
    return (item.unit_price + addonsTotal) * item.quantity;
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] pb-32">
      <header className="sticky top-0 z-50 bg-[#0a0a0f] border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => navigate(`/${slug}`)}
                className="text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="font-bold text-white">Carrinho</h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCart}
              className="text-red-400 hover:text-red-300"
            >
              Limpar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-3">
        {cart.items.map((item, index) => (
          <div key={index} className="card-adegga p-4" data-testid={`cart-item-${index}`}>
            <div className="flex justify-between mb-2">
              <div className="flex-1">
                <h3 className="font-semibold text-white">{item.product_name}</h3>
                {item.variant_name && (
                  <p className="text-sm text-white/60">{item.variant_name}</p>
                )}
                {item.addons?.length > 0 && (
                  <div className="text-sm text-white/50 mt-1">
                    {item.addons.map(addon => (
                      <span key={addon.id} className="mr-2">
                        + {addon.quantity}x {addon.name}
                      </span>
                    ))}
                  </div>
                )}
                {item.notes && (
                  <p className="text-sm text-white/40 italic mt-1">"{item.notes}"</p>
                )}
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeItem(index)}
                className="text-red-400 hover:text-red-300 h-8 w-8"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 bg-white/10"
                  onClick={() => updateQuantity(index, item.quantity - 1)}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center text-white font-medium">{item.quantity}</span>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 bg-white/10"
                  onClick={() => updateQuantity(index, item.quantity + 1)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <span className="text-[#ff6b00] font-bold">
                R$ {getItemTotal(item).toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0f] border-t border-white/5 p-4 z-40">
        <div className="max-w-4xl mx-auto space-y-3">
          <div className="flex justify-between text-lg">
            <span className="text-white/60">Subtotal</span>
            <span className="text-white font-bold">R$ {getTotal().toFixed(2)}</span>
          </div>
          <Link to={`/${slug}/checkout`}>
            <Button className="w-full btn-primary h-12 text-lg" data-testid="checkout-btn">
              Continuar
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
